/** @jsxImportSource @emotion/react */
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Field, Form as FinalForm } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import createDecorator from 'final-form-focus'
import arrayMutators from 'final-form-arrays'
import { FormApi } from 'final-form'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { omit, orderBy } from 'lodash'
import { Link } from 'react-router-dom'
import { OnChange } from 'react-final-form-listeners'
import { CreateQuestionPassageInput, QuestionTypeEnum, SubjectEnumType } from '__generatedTypes__/globalTypes'
import { InputField, NumberInputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { MultiSelectField } from 'components/MultiSelect'
import { EditorField, prepareEditorHtml } from 'components/Editor'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { validateRequired, validateRequiredHtml } from 'utils/validation'
import { theme } from 'theme/theme'
import { setFieldData } from 'utils/setFieldData'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { findOneQuestionPassage_findOneQuestionPassage as QuestionPassage } from '__generatedTypes__/findOneQuestionPassage'
import { findQuestionPassageLocation_findQuestionPassageLocation as QuestionPassageLocation } from '__generatedTypes__/findQuestionPassageLocation'
import TabsNavigation from 'components/TabsNavigation'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import AnswersFieldArray from 'pagesNew/Questions/Form/AnswersFieldArray'
import { MIN_ANSWERS_COUNT } from 'pagesNew/Questions/Form/AnswersFieldArray/AnswersFieldArray'
import { DEFAULT_ANSWERS, QUESTION_TYPES, QuestionType } from 'pagesNew/Questions/Form/consts'
import * as commonClasses from 'pagesNew/Questions/Form/styles'
import { CARSBoosterCategory } from 'constants/consts'
import { QUESTION_MEDIA_PATH_S3 } from 'constants/images'
import { TabPanel } from 'components/TabPanel/TabPanel'
import Badge from 'components/Badge'
import { Revision, Revisions } from 'components/Revisions'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import * as editorClasses from './commonStyles'
import * as classes from './styles'
import RemoveButton from './RemoveButton'
import {
  Subject,
  LEVEL_DIFFICULTY,
  LevelOption,
  MAX_QUESTIONS_COUNT,
  CARS_MINI_PASSAGE_GAME,
  getQuestionsCount,
  validateRequireQuestionsCount,
} from './consts'
import TemplateForm from './TemplateForm'

const TABS = Array.from({ length: MAX_QUESTIONS_COUNT }, (_, i) => i + 1).map((i) => ({
  id: String(i),
  title: `Question ${i}`,
}))

const getEmptyQuestions = (count: number) =>
  Array.from({ length: count }, () => ({
    questionAsHtml: '',
    answer: DEFAULT_ANSWERS,
    answerExplanationAsHtml: '',
    type: QuestionTypeEnum.SingleChoiceCARS,
  }))

type FormState = {
  id?: string
  isTemplate: boolean
  templateName?: string | null
  title: string
  content: string
  subject: Subject
  level: LevelOption[]
  resetStatistics?: boolean
  questionType: QuestionType
  questions: {
    id?: string
    questionAsHtml: string
    answer: {
      id?: string
      textAsHtml: string
      isCorrect: boolean
    }[]
    answerExplanationAsHtml: string
  }[]
  questionsCount: number
  history?: QuestionPassage['history']
}

export type FormStateApi = FormApi<FormState, Partial<FormState>>

type Props = {
  passageQuestion?: QuestionPassage
  passageQuestionLocations?: QuestionPassageLocation[]
  onSubmit: (question: CreateQuestionPassageInput, formApi: FormStateApi) => Promise<void>
  disableDeleteButton?: boolean
  isTemplate?: boolean
}

const focusOnErrors = createDecorator<FormState, Partial<FormState>>()

export const getInitialValues = (passageQuestion?: QuestionPassage, template?: QuestionPassage): Partial<FormState> => {
  const passageQuestionData = template ?? passageQuestion

  if (!passageQuestionData) {
    return {
      title: '',
      content: '',
      subject: undefined,
      level: undefined,
      questions: getEmptyQuestions(MAX_QUESTIONS_COUNT),
      questionsCount: MAX_QUESTIONS_COUNT,
      questionType: QUESTION_TYPES.find(({ id }) => id === QuestionTypeEnum.SingleChoiceCARS),
    }
  }

  const questionsCount = getQuestionsCount(passageQuestionData.subject, passageQuestionData.questions.length)

  const orderedQuestions = orderBy(passageQuestionData.questions, 'order')
    .map((question) => {
      const updatedQuestion = {
        ...question,
        answer: orderBy(question.answer, 'order').map((answer) => (template ? omit(answer, 'id') : answer)),
      }

      return template ? omit(updatedQuestion, 'id') : updatedQuestion
    })
    .slice(0, questionsCount)

  const questions =
    questionsCount > orderedQuestions.length
      ? [...orderedQuestions, ...getEmptyQuestions(questionsCount - orderedQuestions.length)]
      : orderedQuestions

  return {
    ...passageQuestionData,
    isTemplate: passageQuestion?.isTemplate,
    templateName: passageQuestion?.templateName,
    history: passageQuestion?.history ?? [],
    level: LEVEL_DIFFICULTY.filter(({ id }) => passageQuestionData.level?.includes(id)),
    questionType: QUESTION_TYPES.find(({ id }) => id === QuestionTypeEnum.SingleChoiceCARS),
    questions,
    questionsCount,
  }
}

const prepareForSubmit = async (
  values: Partial<FormState>,
  templateName?: string
): Promise<CreateQuestionPassageInput> => {
  const questionsCount = getQuestionsCount(values.subject, values.questionsCount)

  const [contentAsHtml, formattedQuestions] = await Promise.all([
    prepareEditorHtml(values.content ?? '', QUESTION_MEDIA_PATH_S3),
    Promise.all(
      values.questions?.slice(0, questionsCount).map(async (question, index) => {
        const questionAsHtml = await prepareEditorHtml(question.questionAsHtml ?? '', QUESTION_MEDIA_PATH_S3)
        const answerExplanationAsHtml = await prepareEditorHtml(
          question.answerExplanationAsHtml ?? '',
          QUESTION_MEDIA_PATH_S3
        )
        const answers = await Promise.all(
          question.answer?.map(async (answer, answerIndex) => {
            const textAsHtml = await prepareEditorHtml(answer.textAsHtml ?? '', QUESTION_MEDIA_PATH_S3)

            const updatedAnswer = {
              id: answer.id,
              point: +answer.isCorrect,
              textAsHtml,
              isCorrect: answer.isCorrect,
              order: answerIndex,
            }

            return templateName ? omit(updatedAnswer, 'id') : updatedAnswer
          }) ?? []
        )

        const updatedQuestion = {
          id: question.id,
          title: `${values.title} - Question ${index + 1}`,
          isTemplate: templateName ? true : values.isTemplate ?? false,
          templateName: values.templateName || templateName || undefined,
          subjects: [{ id: values.subject?.id ?? '' }],
          type: QuestionTypeEnum.SingleChoiceCARS,
          questionCategory: {
            id: CARSBoosterCategory,
          },
          resetStatistics: templateName ? false : values.resetStatistics ?? false,
          questionAsHtml,
          answerExplanationAsHtml,
          answer: answers,
          order: index,
        }

        return templateName ? omit(updatedQuestion, 'id') : updatedQuestion
      }) ?? []
    ),
  ])

  return {
    title: values.title,
    isTemplate: templateName ? true : values.isTemplate ?? false,
    templateName: values.templateName || templateName || undefined,
    history: templateName ? [] : values.history?.map(({ updatedAt, updatedBy }) => ({ updatedAt, updatedBy })) ?? [],
    content: contentAsHtml,
    subject: {
      id: values.subject?.id ?? '',
    },
    level: values.level?.map(({ id }) => id) ?? [],
    questions: formattedQuestions,
  }
}

export const Form = ({
  passageQuestion,
  passageQuestionLocations,
  onSubmit,
  disableDeleteButton = false,
  isTemplate = false,
}: Props) => {
  const { getLink } = useLink()
  const [submitting, setSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [initialValues, setInitialValues] = useState<Partial<FormState> | undefined>()
  const initialCorrectAnswerId = useMemo(
    () => passageQuestion?.questions?.find(({ answer }) => answer.find(({ isCorrect }) => isCorrect))?.id,
    [passageQuestion]
  )
  const [isCorrectAnswerChanged, setIsCorrectAnswerChanged] = useState(false)
  const [removeError, setRemoveError] = useState<boolean>(false)
  const [subjects, setSubjects] = useState<Subject[]>([])
  const { subjects: allSubjects, loading: subjectsLoading } = usePlatformsAndSubjects({})

  const [activeTab, setActiveTab] = useState(TABS[0])
  const currentQuestion = useMemo(
    () => passageQuestion?.questions[Number(activeTab.id) - 1],
    [passageQuestion, activeTab]
  )

  useEffect(() => {
    if (subjectsLoading) {
      return
    }

    ;(async () => {
      setRemoveError(false)
      const filteredSubjects = allSubjects.filter((subject) => subject.type === SubjectEnumType.Game)
      setInitialValues(getInitialValues(passageQuestion))
      setSubjects(filteredSubjects)
    })()
  }, [passageQuestion, allSubjects, subjectsLoading])

  const handleLoadTemplate = useCallback(
    async (template: QuestionPassage) => {
      const initialValues = await getInitialValues(passageQuestion, template)
      setInitialValues({
        ...initialValues,
        resetStatistics: Boolean(initialCorrectAnswerId),
      })
      setIsCorrectAnswerChanged(Boolean(initialCorrectAnswerId))
    },
    [passageQuestion, initialCorrectAnswerId]
  )

  const handleCorrectAnswerChange = useCallback(
    (form: FormStateApi) => {
      if (!initialCorrectAnswerId) {
        return
      }

      const currentAnswerValue = currentQuestion?.answer
      const currentCorrectAnswerId = currentAnswerValue?.find(({ isCorrect }) => isCorrect)?.id

      const isCorrectAnswerChanged = Boolean(
        initialCorrectAnswerId && initialCorrectAnswerId !== currentCorrectAnswerId
      )
      setIsCorrectAnswerChanged(isCorrectAnswerChanged)
      if (isCorrectAnswerChanged) {
        form.change('resetStatistics', true)
      }
    },
    [initialCorrectAnswerId, currentQuestion]
  )

  const handleSave = useCallback(
    async (values: FormState, formApi: FormStateApi) => {
      setSubmitting(true)
      setIsModalOpen(false)
      try {
        const questionData = await prepareForSubmit(values)
        await onSubmit(questionData, formApi)
        setIsCorrectAnswerChanged(false)
      } catch (e) {
        toast.error('Failed to save question')
      }
      setSubmitting(false)
    },
    [onSubmit]
  )

  const handleSubmit = useCallback(
    async (values: FormState, formApi: FormStateApi) => {
      if (values.resetStatistics) {
        setIsModalOpen(true)
      } else {
        return handleSave(values, formApi)
      }
    },
    [handleSave]
  )

  const renderTitle = () => {
    if (isTemplate) {
      return 'Modify CARS Question Template'
    }

    return `${passageQuestion ? 'Edit' : 'Create'} CARS Question`
  }

  const handleSubjectChange = useCallback(async (subject: Subject | null | undefined, form: FormStateApi) => {
    const questionsCount = getQuestionsCount(subject, form.getState().values?.questionsCount)
    form.change('questionsCount', questionsCount)
  }, [])

  const handleQuestionsCountChange = useCallback(
    async (questionsCount: number, form: FormStateApi) => {
      const currentQuestions = form.getState().values?.questions.slice(0, questionsCount) ?? []

      const questionsWithInitialQuestions: FormState['questions'] =
        questionsCount > currentQuestions.length
          ? [...currentQuestions, ...(initialValues?.questions ?? []).slice(currentQuestions.length, questionsCount)]
          : currentQuestions
      const questionsWithEmptyQuestions: FormState['questions'] =
        questionsCount > questionsWithInitialQuestions.length
          ? [
              ...questionsWithInitialQuestions,
              ...getEmptyQuestions(questionsCount - questionsWithInitialQuestions.length),
            ]
          : questionsWithInitialQuestions

      form.change('questions', questionsWithEmptyQuestions)

      setActiveTab((prev) => (Number(prev.id) > questionsCount ? TABS[0] : prev))
    },
    [initialValues?.questions]
  )

  if (!initialValues) {
    return <PageLoader />
  }

  return (
    <div css={[commonClasses.wrapper, classes.wrapper]}>
      <h1 css={commonClasses.mainTitle}>{renderTitle()}</h1>
      <FinalForm<FormState>
        mutators={{ ...arrayMutators, setFieldData }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, initialValues, submitting: formSubmitting, form, values }) => (
          <form
            onSubmit={(event) => {
              const invalidField = form
                .getRegisteredFields()
                .find((field) => form.getFieldState(field as keyof FormState)?.invalid)

              if (invalidField && invalidField.startsWith('questions')) {
                const tabIndex = invalidField?.match(/questions\.(\d+)\./)?.[1]
                if (tabIndex) {
                  setActiveTab(TABS[Number(tabIndex)])
                }
              }

              handleSubmit(event)
            }}
          >
            <OnChange name="subject">{(subject) => handleSubjectChange(subject, form)}</OnChange>
            <OnChange name="questionsCount">
              {(questionsCount) => questionsCount && handleQuestionsCountChange(questionsCount, form)}
            </OnChange>
            <div css={commonClasses.contentWrapper}>
              <div css={commonClasses.content}>
                {isTemplate && (
                  <InputField
                    name="templateName"
                    validate={validateRequired}
                    label="Template Name"
                    placeholder="Enter template name"
                    fullWidth
                    css={commonClasses.templateName}
                  />
                )}
                <InputField
                  name="title"
                  validate={validateRequired}
                  label="Question Title"
                  placeholder="Enter Title"
                  fullWidth
                />

                <div css={classes.selectsWrapper}>
                  <SelectField
                    name="subject"
                    validate={validateRequired}
                    items={subjects}
                    label="Game Category"
                    placeholder="Choose Game Category"
                    getItemLabel={({ tabName }) => tabName}
                    renderItem={({ tabName }) => tabName}
                    displaySelectedItemsFirst
                    changeImmediately
                    fullWidth
                  />
                  <MultiSelectField
                    name="level"
                    validate={validateRequired}
                    items={LEVEL_DIFFICULTY}
                    label="Level Difficulty"
                    placeholder="Choose Level"
                    displaySelectedItemsFirst
                    fullWidth
                  />
                </div>

                <EditorField
                  type="passage"
                  name="content"
                  label="Passage"
                  validate={validateRequiredHtml}
                  placeholder="Enter Passage"
                  css={editorClasses.editor}
                />

                {values.subject?.slug === CARS_MINI_PASSAGE_GAME && (
                  <NumberInputField
                    name="questionsCount"
                    validate={validateRequireQuestionsCount}
                    label="Questions Count"
                    placeholder="Enter Questions Count"
                    css={classes.questionsCount}
                    fullWidth
                  />
                )}

                <TabsNavigation
                  css={classes.tabsBlock}
                  tabs={TABS.slice(0, values.questionsCount || values.questions.length)}
                  value={activeTab}
                  onChange={setActiveTab}
                />

                {values.questions.map((question, index) => (
                  <TabPanel key={index} value={Number(activeTab.id) - 1} index={index}>
                    <EditorField
                      type="question"
                      name={`questions.${index}.questionAsHtml`}
                      label={`Question #${activeTab.id}`}
                      validate={validateRequiredHtml}
                      placeholder="Enter Question Details"
                      css={editorClasses.editor}
                    />

                    <div css={commonClasses.title}>Option Choices</div>
                    <FieldArray
                      name={`questions.${index}.answer`}
                      validate={(value) =>
                        (value?.length ?? 0) < MIN_ANSWERS_COUNT
                          ? `Should be at least ${MIN_ANSWERS_COUNT} options`
                          : !value?.some(({ isCorrect }) => isCorrect)
                          ? 'One option must be the correct answer'
                          : undefined
                      }
                    >
                      {({ fields, meta }) => (
                        <AnswersFieldArray
                          fields={fields}
                          meta={meta}
                          onAnswerChange={() => handleCorrectAnswerChange(form)}
                        />
                      )}
                    </FieldArray>

                    <div css={commonClasses.title}>Solution</div>
                    <div css={commonClasses.note}>
                      <b>Note:</b> If you want to use the platform name in the solution text, you can use the &quot;
                      {'{%PLATFORM_SHORT_NAME%}'}&quot; template, which will automatically be replaced with the short
                      name of the current platform when displayed to the user (e.g. &quot;DAT&quot; for DATBooster or
                      &quot;OAT&quot; for OATBooster). You can type this yourself or insert via &quot;Platform&quot;
                      button in Editor Toolbar.
                    </div>
                    <EditorField
                      type="solution"
                      name={`questions.${index}.answerExplanationAsHtml`}
                      label="Solution Details"
                      validate={validateRequiredHtml}
                      placeholder="Enter Solution Details"
                      css={editorClasses.editor}
                    />
                  </TabPanel>
                ))}

                {!!initialValues.history?.length && (
                  <Revisions revisions={(initialValues?.history ?? []) as Revision[]} />
                )}
              </div>

              <div css={commonClasses.blocksWrapper}>
                <div css={commonClasses.block}>
                  <h4>Question Type</h4>
                  <SelectField
                    name="questionType"
                    items={QUESTION_TYPES}
                    placeholder="Choose Question Type"
                    disabled
                    isNotNullable
                    fullWidth
                  />
                  <Button variant="outlined" color="blue" type="button" disabled>
                    Load Type
                  </Button>
                </div>

                {!isTemplate && (
                  <div css={commonClasses.block}>
                    <TemplateForm
                      getTemplateData={(templateName) => prepareForSubmit(form.getState().values, templateName)}
                      setTemplateData={handleLoadTemplate}
                      isLoadTemplateDisabled={!!initialValues.id}
                    />
                  </div>
                )}

                <div css={commonClasses.block}>
                  <h4>Publish</h4>
                  {!isTemplate && !!initialCorrectAnswerId && (
                    <div>
                      <Field name="resetStatistics" type="checkbox">
                        {({ input }) => (
                          <FormControlLabel
                            label="Reset Statistics"
                            control={<Checkbox {...input} />}
                            disabled={isCorrectAnswerChanged}
                            css={commonClasses.checkbox}
                          />
                        )}
                      </Field>
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="blue"
                    disabled={formSubmitting || submitting || subjectsLoading}
                  >
                    {isTemplate ? 'Save' : 'Publish'}
                  </Button>
                  {!!initialValues.id && !disableDeleteButton && (
                    <RemoveButton
                      questionId={initialValues?.id ?? ''}
                      submitting={formSubmitting || submitting}
                      setSubmitting={setSubmitting}
                      setRemoveError={setRemoveError}
                      isTemplate={isTemplate}
                    />
                  )}
                  {removeError && (
                    <Typography variant="body2" css={commonClasses.badge(theme.colors.red)}>
                      The question is used either in a CARS Question Bank
                    </Typography>
                  )}
                </div>

                {!!initialValues.id && !isTemplate && (
                  <div css={commonClasses.block}>
                    <h4>CARS Question Location</h4>
                    {passageQuestionLocations?.length ? (
                      <div css={commonClasses.questionLocations}>
                        {passageQuestionLocations?.map(({ id, title }) => (
                          <Link
                            key={id}
                            to={getLink(cmsRoutes.CARS_QUESTION_BANK_EDIT, { carsQuestionBankId: id })}
                            target="_blank"
                            css={classes.link}
                          >
                            <Badge text={title} color="#2489FF" />
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div css={commonClasses.empty}>Nothing found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <ConfirmationModal
              open={isModalOpen}
              title="Reset Statistics"
              subtitle={
                <>
                  Are you sure you want to reset the statistics for <br />
                  this question? This action cannot be changed.
                </>
              }
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => handleSave(form.getState().values, form)}
              confirmText="Confirm Reset & Publish"
              closeText="Cancel"
            />
          </form>
        )}
      />
    </div>
  )
}
